/** A mode the UI can actually render in. */
export type ThemeMode = 'light' | 'dark'

/** What the user picks: a fixed mode, or follow the operating system's setting. */
export type ThemePreference = ThemeMode | 'system'

export const THEME_PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system']

/** The mode every user sees when dark mode is disabled, and the default for new users. */
export const DEFAULT_THEME_MODE: ThemeMode = 'light'

/**
 * Master switch for dark mode. While `true`, the header shows the light/dark/system picker
 * and the user's persisted choice is applied. Set to `false` to hide the picker and force
 * every user back to DEFAULT_THEME_MODE, whatever they chose earlier (their stored choice is
 * kept, so re-enabling restores it).
 *
 * Deliberately a static constant rather than a build-time env variable: the build that
 * flips it off ships the same behavior to every environment.
 */
export const DARK_MODE_ENABLED: boolean = false

const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)'

function systemDarkQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null
  return window.matchMedia(SYSTEM_DARK_QUERY)
}

/** The operating system's current color scheme. Light where it cannot be read. */
export function getSystemThemeMode(): ThemeMode {
  return systemDarkQuery()?.matches ? 'dark' : 'light'
}

/** Calls `onChange` whenever the operating system's color scheme changes. Returns the unsubscribe. */
export function subscribeToSystemThemeMode(onChange: () => void): () => void {
  const query = systemDarkQuery()
  if (!query) return () => {}
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

/**
 * The mode actually applied for a stored preference, honoring DARK_MODE_ENABLED.
 * `forceLight` is set while a light-only surface (the admin panel) is shown.
 */
export function resolveThemeMode(
  preference: ThemePreference,
  forceLight = false,
  systemMode: ThemeMode = getSystemThemeMode(),
): ThemeMode {
  if (!DARK_MODE_ENABLED || forceLight) return DEFAULT_THEME_MODE
  return preference === 'system' ? systemMode : preference
}

/** Points the CSS token overrides in index.css (`html[data-theme='dark']`) at `mode`. */
export function applyThemeModeToDocument(mode: ThemeMode): void {
  document.documentElement.setAttribute('data-theme', mode)
}
